<template>
	<div class="menu-more" :style="menuStyle" v-show="visible">
		<ul class="menu-more__list">
			<!-- DEV NOTE: This needs to apply the "is-active" class when an item is selected
			-->
			<DropdownMenuItem
				v-for="item in optionsList"
				:key="item.value"
				:text="item.text"
				:value="item.value"
				:iconClass="item.iconClass"
				:disabled="item.disabled"
				@itemClick="opts => onSelect(opts, item.clickHandler)"
				:defaultItemClass="menuItemClass"
				:isOpen="item.isOpen"
				:variant="item.options && item.options.length && 'dropdown'"
				@itemExpanded="itemExpanded"
			>
				<template v-slot:dropdownValues>
					<ul
						class="menu-more__list menu-more__list--nested"
						v-if="item.options"
					>
						<DropdownMenuItem
							v-for="child in item.options"
							:key="child.value"
							:text="child.text"
							:value="child.value"
							:iconClass="child.iconClass"
							:disabled="child.disabled"
							:defaultItemClass="menuItemClass"
							class="menu-more__list-item--nested"
							@itemClick="
								opts =>
									onChildClick(
										opts,
										child.clickHandler || item.clickHandler
									)
							"
							:selectedValue="selectedDropdownValue"
						/>
					</ul>
				</template>
			</DropdownMenuItem>
		</ul>
	</div>
</template>

<script>
import baseComponentMixin from '@/mixins/base-component-mixin';
import DropdownMenuItem from './menu-dropdown-item.vue';
import { cloneDeep } from 'lodash';

export default {
	name: 'MenuMore',
	mixins: [baseComponentMixin],
	components: {
		DropdownMenuItem
	},
	data() {
		return {
			menuItemClass: 'menu-more__list-item',
			selectedDropdownValue: undefined,
			expandedOption: { value: null, expanded: null }
		};
	},
	props: {
		/**
		 * A menu option.
		 * @typedef {Object} Option
		 * @property {String} text
		 * @property {String|Number} value
		 * @property {String} iconClass
		 * @property {boolean} disabled
		 */

		/**
		 * @type Array
		 */
		options: {
			type: Array,
			default: () => []
		},
		value: {
			type: Object
		},
		visible: {
			type: Boolean,
			default: false
		},
		minWidth: {
			type: String,
			default: 'auto'
		}
	},
	methods: {
		onSelect(option, clickHandler) {
			this.selectedDropdownValue = option.value;
			if (clickHandler) {
				clickHandler(option);
			}

			this.$emit('input', option);
			this.$emit('change', option);
		},
		onChildClick(option, clickHandler) {
			this.onSelect(option);
			if (clickHandler) {
				clickHandler(option);
			}
		},
		itemExpanded(item) {
			if (this.visible) {
				this.expandedOption = item;
			}
		}
	},
	computed: {
		menuStyle() {
			return {
				minWidth: this.minWidth
			};
		},
		optionsList() {
			let list = [...this.options.map(object => cloneDeep(object))];

			list.forEach(o => {
				o.isOpen =
					this.expandedOption.value === o.value &&
					this.expandedOption.expanded === true
						? true
						: false;
			});

			return list;
		}
	},
	watch: {
		visible: {
			handler(val) {
				if (!val) {
					this.expandedOption = { value: null, expanded: null };
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('borderless');
.menu-more {
	display: inline-block;
	width: 225px;
	font-size: $btn-reduced-font-size;
	max-height: 500px;
	overflow-y: auto;
	z-index: $z-level1-5;
	text-transform: uppercase;

	&__list {
		display: inline-flex;
		flex-direction: column;
		padding-top: $padding-sm;
		padding-bottom: $padding-sm;
		width: 100%;
		&--nested {
			display: none; // changes to inline-flex when open
			background: var(--bg-brand-2-tint);
			color: var(--bg-brand-2-tint-fg-2);
			.is-open & {
				display: inline-flex;
			}
		}
	}

	&__list-item {
		display: inline-flex;
		flex-direction: column;
		&.is-open {
			margin-bottom: $gutter-md;
		}
	}

	&:deep(.menu-item) {
		display: inline-flex;
		align-items: center;
		justify-content: space-between;
		gap: $gutter-md;
		cursor: pointer;
		padding: $padding-sm $padding-md;
		.menu-item__text {
			border-bottom: 1px solid transparent;
		}
		.menu-item__arrow {
			margin-bottom: 2px; // adjusts for text size and bottom border for vertical alignment
		}
	}
}

/* LL styles v1 Only */
.menu-more {
	background: var(--bg-brand-1);
	color: var(--bg-brand-1-fg-2);

	&:deep(.menu-item) {
		&:hover,
		&:focus,
		&:active {
			color: var(--link-hover);
			.menu-item__text {
				border-color: var(--link-hover);
			}
		}
	}
}

/* LL styles v2 Only */
/* .menu-more {
	background: var(--bg-brand-2);
	color: var(--bg-brand-2-fg-2);

	&:deep( .menu-item ) {
		&:hover,
		&:focus,
		&:active {
			.menu-item__text {
				color: var(--bg-brand-2-fg-2);
				border-color: var(--bg-brand-2-fg-2);
			}
		}
	}

	&__list--nested {
		&:deep( .menu-item ) {
			&:hover,
			&:focus,
			&:active {
				.menu-item__text {
					border-color: var(--bg-brand-2-tint-fg-2);
					color: var(--bg-brand-2-tint-fg-2);
				}
			}
		}
	}
} */
</style>
